import type { ParticipantLook, ParticipantRole } from './presenceSource';

/** A participant's colour and the colour of text drawn on it. */
export interface ParticipantColor { readonly color: string; readonly ink: string }

const BLACK = '#000000', WHITE = '#FFFFFF';
/** The app's `AvatarColorAssigner` palette (its five priority colours, then five regular ones), then ten of the web's own. */
export const PARTICIPANT_PALETTE: readonly ParticipantColor[] = ([
  ['#F8ED15', BLACK], ['#742AED', WHITE], ['#F79009', WHITE], ['#F34439', WHITE], ['#EF0AFF', WHITE],
  ['#FFC935', BLACK], ['#4BD3CE', BLACK], ['#86231C', WHITE], ['#D1A0E6', BLACK], ['#2C4700', WHITE],
  ['#0891B2', WHITE], ['#A3E635', BLACK], ['#7C4A2D', WHITE], ['#64748B', WHITE], ['#FDBA74', BLACK],
  ['#1E3A8A', WHITE], ['#6B8E23', WHITE], ['#93C5FD', BLACK], ['#D4A373', BLACK], ['#4C1D95', WHITE],
] as const).map(([color, ink]) => ({ color, ink }));
export const PRIORITY_COUNT = 5;

const utf8 = new TextEncoder();
/** The app's `StableHash`: 32-bit FNV-1a over the key's UTF-8 bytes, so a key prefers the same slot on every client. */
export function preferredSlot(key: string): number {
  let hash = 2166136261;
  for (const byte of utf8.encode(key)) hash = Math.imul(hash ^ byte, 16777619) >>> 0;
  return hash % PARTICIPANT_PALETTE.length;
}

function leastUsed(preferred: number, usage: readonly number[], start: number, end: number): number {
  const size = end - start;
  let best = start + preferred % size;
  for (let offset = 1; offset < size; offset++) {
    const candidate = start + (preferred + offset) % size;
    if (usage[candidate] < usage[best]) best = candidate;
  }
  return best;
}

/** The app's `FreeSlot`: the least used slot near the preferred one, a priority colour unless a regular one is used less. */
export function freeSlot(preferred: number, usage: readonly number[]): number {
  const priority = leastUsed(preferred, usage, 0, PRIORITY_COUNT);
  const regular = leastUsed(preferred, usage, PRIORITY_COUNT, PARTICIPANT_PALETTE.length);
  return usage[regular] < usage[priority] ? regular : priority;
}

/** The colour a key prefers, for a participant outside any roster. */
export const participantColor = (key: string): ParticipantColor => PARTICIPANT_PALETTE[preferredSlot(key)];

/**
 * The colours of a session's participants, as the app's `SessionAvatarColorRegistry` gives them: a newcomer, in join
 * order, takes the free slot nearest the one it prefers among the colours of those present. Unlike the app, someone who
 * leaves and comes back keeps their colour, unless a newcomer took it meanwhile and another colour is less used.
 */
export class ParticipantColors {
  private slots = new Map<string, number>();
  private present = new Set<string>();

  sync(roster: readonly { key: string; order: number }[]): void {
    const keys = new Set(roster.map(entry => entry.key));
    const usage = PARTICIPANT_PALETTE.map(() => 0);
    const arriving = new Map<string, number>();
    for (const { key, order } of roster) if (!this.present.has(key) && !arriving.has(key)) arriving.set(key, order);
    for (const key of keys) if (!arriving.has(key)) usage[this.slots.get(key)!]++;
    const ordered = [...arriving].sort(([a, orderA], [b, orderB]) => orderA - orderB || (a < b ? -1 : a > b ? 1 : 0));
    for (const [key] of ordered) {
      const kept = this.slots.get(key);
      const slot = kept !== undefined && usage[kept] <= Math.min(...usage) ? kept : freeSlot(preferredSlot(key), usage);
      this.slots.set(key, slot);
      usage[slot]++;
    }
    this.present = keys;
  }

  reset(): void {
    this.slots.clear();
    this.present.clear();
  }

  colorOf(key: string): ParticipantColor {
    const slot = this.slots.get(key);
    return slot === undefined ? participantColor(key) : PARTICIPANT_PALETTE[slot];
  }
}

/** One page's participants, whichever panel draws them: the live room's pointers, the chat and the share dialog. */
export const sessionColors = new ParticipantColors();

/** Your own avatar where nothing more is known of you. */
export const OWN_LOOK: ParticipantLook = { color: '#57616c', agent: false, role: 'owner' };
/** A person no source describes: a plain person in a colour of their own. */
export function plainLook(key: string, role: ParticipantRole = 'editor', avatar?: string): ParticipantLook {
  return { ...sessionColors.colorOf(key), agent: false, role, ...(avatar ? { avatar } : {}) };
}
